import { parseRef } from '@promanage/core';

/**
 * A transaction's number, from whatever somebody typed for it — or null when it is not one.
 *
 * `TX012345`, `tx 12345` and `tx-12345` are all the ref (`parseRef` is forgiving on purpose), and
 * so is a bare `12345`: in a box labelled as a transaction, the prefix is the part a person
 * leaves off. A ref for something ELSE — `PR000042` — is refused rather than read as transaction
 * 42, because that is somebody pasting the wrong thing and a silent match would show them a
 * plausible list of the wrong rows.
 */
export function transactionSeq(input: string): number | null {
  const text = input.trim();
  if (/^\d{1,12}$/.test(text)) {
    const sequence = Number(text);
    return Number.isSafeInteger(sequence) && sequence >= 1 ? sequence : null;
  }
  const parsed = parseRef(text);
  return parsed?.entity === 'transaction' ? parsed.sequence : null;
}

/**
 * What stands in the way of applying a typed run, as a sentence — or null when it can be applied.
 *
 * An empty end is not a fault: one end alone is "everything from" or "everything up to".
 */
export function refRangeIssue(from: string, to: string): string | null {
  const first = from.trim() === '' ? undefined : transactionSeq(from);
  const last = to.trim() === '' ? undefined : transactionSeq(to);
  if (first === null || last === null) return 'Enter a transaction reference, like TX000042';
  if (first !== undefined && last !== undefined && first > last) {
    return 'The last transaction is before the first';
  }
  return null;
}
